/**
 * What the factory store keeps, sorted into the twelve groups the owner named
 * (11 Sep 2026). Before this the category was a free-text box and the list was
 * one person's spelling away from splitting in two — "battery", "Battery" and
 * "Batteries" would each have become their own line on the store summary.
 *
 * The order is the owner's, not alphabetical: it follows the build, from the
 * motor at the front to the wiring that ties it together. A picker shows them
 * in that order because that is the order the store keeper thinks in.
 *
 * The list is the choice a form offers; it is NOT a constraint on the column.
 * An item that already carries something else keeps it, and a pasted line whose
 * category is not on the list keeps its own word rather than being dropped —
 * `matchFactoryCategory` only corrects what it can recognise.
 */
export const FACTORY_CATEGORIES = [
  'Motor & controller',
  'Battery',
  'Charger',
  'Front assembly kit',
  'Differential',
  'Brake system',
  'Handle system',
  'Seat',
  'Tyre & tube',
  'Lights',
  'Accessories',
  'Wiring',
] as const;

export type FactoryCategory = (typeof FACTORY_CATEGORIES)[number];

/** Letters and digits only, so "Tyre & tube", "tyre/tube" and "TYRE TUBE" meet. */
function squash(text: string): string {
  return text.toUpperCase().replace(/[^A-Z0-9]+/g, '');
}

/**
 * The words a person actually types for each group — the singular, the second
 * half of a pair, the shop-floor shorthand. A paste reading "motor, 7" is a
 * motor; so is one reading "controller".
 */
const SPOKEN: Record<string, FactoryCategory> = {
  MOTOR: 'Motor & controller',
  MOTORS: 'Motor & controller',
  CONTROLLER: 'Motor & controller',
  CONTROLLERS: 'Motor & controller',
  MOTORCONTROLLER: 'Motor & controller',
  BATTERY: 'Battery',
  BATTERIES: 'Battery',
  CELL: 'Battery',
  CHARGER: 'Charger',
  CHARGERS: 'Charger',
  FRONTASSEMBLY: 'Front assembly kit',
  FRONTASSEMBLYKIT: 'Front assembly kit',
  FRONTKIT: 'Front assembly kit',
  ASSEMBLYKIT: 'Front assembly kit',
  DIFFERENTIAL: 'Differential',
  DIFFERENTIALS: 'Differential',
  DIFF: 'Differential',
  BRAKE: 'Brake system',
  BRAKES: 'Brake system',
  BRAKESYSTEM: 'Brake system',
  HANDLE: 'Handle system',
  HANDLES: 'Handle system',
  HANDLESYSTEM: 'Handle system',
  SEAT: 'Seat',
  SEATS: 'Seat',
  TYRE: 'Tyre & tube',
  TYRES: 'Tyre & tube',
  TIRE: 'Tyre & tube',
  TIRES: 'Tyre & tube',
  TUBE: 'Tyre & tube',
  TUBES: 'Tyre & tube',
  TYRETUBE: 'Tyre & tube',
  LIGHT: 'Lights',
  LIGHTS: 'Lights',
  LAMP: 'Lights',
  LAMPS: 'Lights',
  ACCESSORY: 'Accessories',
  ACCESSORIES: 'Accessories',
  ACCESSORIE: 'Accessories',
  WIRING: 'Wiring',
  WIRE: 'Wiring',
  WIRES: 'Wiring',
  HARNESS: 'Wiring',
  WIRINGHARNESS: 'Wiring',
};

const BY_SQUASHED = new Map<string, FactoryCategory>([
  ...FACTORY_CATEGORIES.map((category) => [squash(category), category] as const),
  ...Object.entries(SPOKEN).map(([word, category]) => [squash(word), category] as const),
]);

/**
 * The group a typed word belongs to, or the word itself when it belongs to none.
 *
 * Never returns a category the caller did not mean: an unrecognised word comes
 * back trimmed and unchanged, so a store keeper can still file something the
 * list does not cover and the owner sees it on the summary rather than losing it.
 */
export function matchFactoryCategory(text: string | null | undefined): string | null {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return null;
  return BY_SQUASHED.get(squash(trimmed)) ?? trimmed;
}

/** Is this one of the twelve? */
export function isFactoryCategory(text: string | null | undefined): text is FactoryCategory {
  return !!text && (FACTORY_CATEGORIES as readonly string[]).includes(text);
}

// ---------------------------------------------------------------------------
// In and out, properly (owner, 20 Oct 2026: "proper in and out system need to
// be build"; asked what every entry records: "From whom / for what"). Every
// IN says who it came from (and the bill, for a purchase); every OUT says what
// it was for and who took it. A second count per item — the faulty bin — holds
// the parts taken off vehicles on a replacement.

export const FACTORY_BINS = ['GOOD', 'FAULTY'] as const;
export type FactoryBinKey = (typeof FACTORY_BINS)[number];
export const FACTORY_BIN_LABELS: Record<FactoryBinKey, string> = { GOOD: 'Good stock', FAULTY: 'Faulty bin' };

export const FACTORY_PURPOSES = [
  'PURCHASE',
  'RETURN',
  'REPLACED_PART',
  'REPAIRED',
  'PRODUCTION',
  'REPLACEMENT',
  'SALE',
  'SCRAP',
  'SUPPLIER_RETURN',
  'OTHER',
  'SWAP',
] as const;
export type FactoryPurposeKey = (typeof FACTORY_PURPOSES)[number];
export const FACTORY_PURPOSE_LABELS: Record<FactoryPurposeKey, string> = {
  PURCHASE: 'Bought from a supplier',
  RETURN: 'Given back unused',
  REPLACED_PART: 'Old part from a replacement',
  REPAIRED: 'Repaired',
  PRODUCTION: 'Production line',
  REPLACEMENT: 'Replacement on a complaint',
  SALE: 'Sold as a spare',
  SCRAP: 'Scrapped',
  SUPPLIER_RETURN: 'Sent back to the supplier',
  OTHER: 'Other',
  SWAP: 'Swapped on a vehicle',
};

/** What the store keeper picks for stock coming in, and going out, of good stock. */
export const FACTORY_IN_PURPOSES: readonly FactoryPurposeKey[] = ['PURCHASE', 'RETURN', 'OTHER'];
export const FACTORY_OUT_PURPOSES: readonly FactoryPurposeKey[] = ['PRODUCTION', 'SALE', 'SCRAP', 'SUPPLIER_RETURN', 'OTHER'];
/** What happens to a part in the faulty bin. REPAIRED moves it back into good stock. */
export const FACTORY_FAULTY_ACTIONS: readonly FactoryPurposeKey[] = ['SUPPLIER_RETURN', 'SCRAP', 'REPAIRED'];

/** Whether an entry put stock in or took it out. */
export const factoryDirection = (delta: number): 'IN' | 'OUT' => (delta >= 0 ? 'IN' : 'OUT');

/** One line of the in/out register. */
export interface FactoryRegisterRow {
  id: string;
  at: string;
  item: { id: string; name: string; unit: string; code: string | null; category: string | null };
  kind: 'OPENING' | 'RECEIVE' | 'ISSUE' | 'COUNT' | 'ADJUST';
  bin: FactoryBinKey;
  direction: 'IN' | 'OUT';
  /** Signed: +12 in, −3 out. */
  delta: number;
  quantityAfter: number;
  /** It happened before the item's last count, so the count holds it: the figure did not move (21 Oct 2026). */
  countedOver: boolean;
  purpose: FactoryPurposeKey | null;
  /** In: the supplier. Out: who took it. */
  party: string | null;
  billNo: string | null;
  chassisNo: string | null;
  /** The complaint a replacement was fitted against. */
  request: { id: string; subject: string } | null;
  note: string | null;
  by: string | null;
}

export interface FactoryRegisterPage {
  data: FactoryRegisterRow[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
  /** Day by day ("YYYY-MM-DD", IST): pieces in and out of good stock, and into the faulty bin. */
  days: Array<{ day: string; in: number; out: number; faultyIn: number }>;
  totals: { in: number; out: number; faultyIn: number; faultyOut: number };
}

/** A receipt from a supplier: many items on one bill. */
export interface FactoryReceiptInput {
  party: string;
  billNo?: string | null;
  note?: string | null;
  lines: Array<{ itemId: string; quantity: number }>;
}

/** Stock out of good stock for one purpose, to one taker: many items at once. */
export interface FactoryIssueInput {
  purpose: FactoryPurposeKey;
  party: string;
  note?: string | null;
  /**
   * The day it went out, "YYYY-MM-DD" (IST), when that was not today — 21 Oct
   * 2026. Yesterday's usage typed after this morning's count is then known to
   * be in the count already, and does not take the shelf down a second time.
   */
  on?: string | null;
  /**
   * Today's usage typed after today's count, when it went out before the
   * count: each part is written just before its count, so the count keeps it.
   */
  beforeCount?: boolean;
  lines: Array<{ itemId: string; quantity: number }>;
}
